/**
 * Shared building blocks for the Banner and Intro Teaser studios:
 * form controls, collapsible sections, undo/redo history and the
 * template bar (channel slot · account default · named templates).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaTrash, FaChevronDown, FaSave, FaFolderOpen, FaStar } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../api';
import {
  listCompositionTemplates,
  getDefaultCompositionTemplate,
  saveCompositionTemplate,
  deleteCompositionTemplate,
  TEMPLATE_TYPES,
  cacheAccountBannerDefault,
  loadCachedAccountBannerDefault,
  cacheChannelBanner,
  loadCachedChannelBanner,
  cacheAccountTeaserDefault,
  loadCachedAccountTeaserDefault,
  cacheChannelTeaser,
  loadCachedChannelTeaser,
} from './compositionTemplates';
import './studio.css';

/* ── Template caches by type (offline fallback) ───────────────── */
export const TEMPLATE_CACHE = {
  [TEMPLATE_TYPES.TRANSITION_BANNER]: {
    cacheAccount: cacheAccountBannerDefault,
    loadAccount: loadCachedAccountBannerDefault,
    cacheChannel: cacheChannelBanner,
    loadChannel: loadCachedChannelBanner,
  },
  [TEMPLATE_TYPES.INTRO_TEASER]: {
    cacheAccount: cacheAccountTeaserDefault,
    loadAccount: loadCachedAccountTeaserDefault,
    cacheChannel: cacheChannelTeaser,
    loadChannel: loadCachedChannelTeaser,
  },
};

/**
 * Load the saved design for the account (channelId empty) or a channel.
 * Server first, this device's cache as fallback. Returns parsed payload or null.
 */
export async function loadSavedDesign({ type, user, channelId, parse }) {
  const cache = TEMPLATE_CACHE[type];
  try {
    const doc = await getDefaultCompositionTemplate({ type, channelId: channelId || undefined });
    const parsed = parse(doc?.payload);
    if (parsed) {
      if (channelId) cache?.cacheChannel(user, channelId, doc.payload);
      else cache?.cacheAccount(user, doc.payload);
      return parsed;
    }
  } catch {
    /* offline — fall back to cache */
  }
  const cached = channelId ? cache?.loadChannel(user, channelId) : cache?.loadAccount(user);
  return cached ? parse(cached) : null;
}

/* ── Undo / redo ───────────────────────────────────────────────── */
/**
 * History around an external value/setter. Rapid changes (slider drags)
 * are coalesced into one step; pass { commit: true } to force a step.
 */
export function useHistory(value, setValue, { limit = 80, coalesceMs = 600 } = {}) {
  const valueRef = useRef(value);
  valueRef.current = value;
  const past = useRef([]);
  const future = useRef([]);
  const lastPush = useRef(0);
  const [, force] = useState(0);

  const set = useCallback((updater, { commit = false } = {}) => {
    const prev = valueRef.current;
    const next = typeof updater === 'function' ? updater(prev) : updater;
    if (next === prev) return;
    const now = Date.now();
    if (commit || now - lastPush.current > coalesceMs) {
      past.current.push(prev);
      if (past.current.length > limit) past.current.shift();
      future.current = [];
      force((n) => n + 1);
    }
    lastPush.current = now;
    valueRef.current = next;
    setValue(next);
  }, [setValue, limit, coalesceMs]);

  /** Start a fresh step for the next change (e.g. on pointer down). */
  const checkpoint = useCallback(() => { lastPush.current = 0; }, []);

  const undo = useCallback(() => {
    if (!past.current.length) return;
    const prev = past.current.pop();
    future.current.push(valueRef.current);
    valueRef.current = prev;
    lastPush.current = 0;
    setValue(prev);
    force((n) => n + 1);
  }, [setValue]);

  const redo = useCallback(() => {
    if (!future.current.length) return;
    const next = future.current.pop();
    past.current.push(valueRef.current);
    valueRef.current = next;
    lastPush.current = 0;
    setValue(next);
    force((n) => n + 1);
  }, [setValue]);

  const reset = useCallback(() => {
    past.current = [];
    future.current = [];
    force((n) => n + 1);
  }, []);

  return {
    set,
    checkpoint,
    undo,
    redo,
    reset,
    canUndo: past.current.length > 0,
    canRedo: future.current.length > 0,
  };
}

/* ── Form controls ─────────────────────────────────────────────── */
let fieldSeq = 0;
const useFieldId = (prefix) => {
  const ref = useRef(null);
  if (!ref.current) { fieldSeq += 1; ref.current = `${prefix}-${fieldSeq}`; }
  return ref.current;
};

export function Section({ title, children, defaultOpen = true, right = null }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={`st-section ${open ? 'is-open' : ''}`}>
      <header className="st-section__head">
        <button type="button" className="st-section__toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          <FaChevronDown className="st-section__chev" aria-hidden="true" />
          <span>{title}</span>
        </button>
        {right}
      </header>
      <div className="st-section__body-wrap">
        <div className="st-section__body">{children}</div>
      </div>
    </section>
  );
}

/** Range + number input. `scale` shows value×scale (e.g. 100 for %). */
export function Slider({
  label, value, min, max, step = 1, onChange, unit = '', scale = 1, decimals, disabled = false,
}) {
  const id = useFieldId('st-slider');
  const v = Number.isFinite(Number(value)) ? Number(value) : min;
  const shownDecimals = decimals ?? (step * scale < 1 ? Math.min(3, String(step * scale).split('.')[1]?.length || 1) : 0);
  const shown = (v * scale).toFixed(shownDecimals);
  const pct = ((v - min) / (max - min || 1)) * 100;
  const [draft, setDraft] = useState(null);

  const commitDraft = () => {
    if (draft == null) return;
    const n = parseFloat(draft);
    if (Number.isFinite(n)) onChange(Math.min(max, Math.max(min, n / scale)));
    setDraft(null);
  };

  return (
    <div className={`st-field st-slider ${disabled ? 'is-disabled' : ''}`}>
      <div className="st-slider__top">
        <label htmlFor={id}>{label}</label>
        <span className="st-slider__value">
          <input
            type="text"
            inputMode="decimal"
            value={draft ?? shown}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitDraft}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setDraft(null); }}
            disabled={disabled}
            aria-label={`${label} value`}
          />
          {unit && <em>{unit}</em>}
        </span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={v}
        disabled={disabled}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{ '--st-fill': `${Math.min(100, Math.max(0, pct))}%` }}
      />
    </div>
  );
}

const normalizeHex = (c, fallback = '#ffffff') => {
  if (typeof c !== 'string') return fallback;
  const s = c.trim();
  if (/^#[0-9a-f]{6}$/i.test(s)) return s.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(s)) return `#${s.slice(1).split('').map((x) => x + x).join('')}`.toLowerCase();
  return fallback;
};

export function ColorField({ label, value, onChange }) {
  const id = useFieldId('st-color');
  const hex = normalizeHex(value);
  const [draft, setDraft] = useState(null);
  return (
    <div className="st-field st-color">
      <label htmlFor={id}>{label}</label>
      <div className="st-color__row">
        <span className="st-color__swatch" style={{ background: hex }}>
          <input id={id} type="color" value={hex} onChange={(e) => onChange(e.target.value)} />
        </span>
        <input
          className="st-color__hex"
          type="text"
          value={draft ?? hex.toUpperCase()}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            if (draft != null) {
              const n = normalizeHex(draft.startsWith('#') ? draft : `#${draft}`, null);
              if (n) onChange(n);
            }
            setDraft(null);
          }}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          aria-label={`${label} hex`}
          spellCheck={false}
        />
      </div>
    </div>
  );
}

export function Toggle({ label, checked, onChange, hint }) {
  return (
    <label className="st-toggle" title={hint}>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="st-toggle__track" aria-hidden="true"><span className="st-toggle__thumb" /></span>
      <span className="st-toggle__label">{label}</span>
    </label>
  );
}

export function Segmented({ label, options, value, onChange }) {
  return (
    <div className="st-field">
      {label && <span className="st-field__label">{label}</span>}
      <div className="st-seg" role="radiogroup" aria-label={label}>
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={value === o.value}
            className={value === o.value ? 'is-active' : ''}
            onClick={() => onChange(o.value)}
            title={o.title || o.label}
          >
            {o.icon || o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function SelectField({ label, value, onChange, options }) {
  const id = useFieldId('st-select');
  return (
    <div className="st-field">
      <label htmlFor={id} className="st-field__label">{label}</label>
      <select id={id} className="st-select" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          o.group ? (
            <optgroup key={o.group} label={o.group}>
              {o.options.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
            </optgroup>
          ) : <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

/* ── Template bar ──────────────────────────────────────────────── */
/**
 * Channel slot (load/save), account default and named templates for one
 * template type. `buildPayload()` returns what to save; `onApply(parsed)`
 * applies a loaded design.
 */
export function TemplateBar({
  type,
  user,
  userToken,
  noun = 'design',
  buildPayload,
  parsePayload,
  onApply,
}) {
  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [busy, setBusy] = useState(false);
  const popRef = useRef(null);
  const cache = TEMPLATE_CACHE[type];

  useEffect(() => {
    if (!userToken) return;
    api.get('/get-channels', { headers: { Authorization: `Bearer ${userToken}` } })
      .then((res) => setChannels(res.data?.channels || []))
      .catch(() => {});
  }, [userToken]);

  const refreshTemplates = useCallback(() => {
    if (!userToken) return;
    setLoadingTemplates(true);
    listCompositionTemplates({ type })
      .then((rows) => setTemplates(rows))
      .catch(() => setTemplates([]))
      .finally(() => setLoadingTemplates(false));
  }, [type, userToken]);

  useEffect(() => { refreshTemplates(); }, [refreshTemplates]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (popRef.current && !popRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const channel = channels.find((c) => c._id === channelId);
  const channelName = channel?.channelName || 'channel';

  const loadChannel = async () => {
    if (!channelId) { toast.error('Pick a channel first'); return; }
    setBusy(true);
    const parsed = await loadSavedDesign({ type, user, channelId, parse: parsePayload });
    setBusy(false);
    if (parsed) {
      onApply(parsed);
      toast.success(`Loaded ${channelName}'s ${noun}`);
    } else {
      toast.info(`${channelName} has no saved ${noun} yet`);
    }
  };

  const saveChannel = async () => {
    if (!channelId) { toast.error('Pick a channel first'); return; }
    const payload = buildPayload();
    setBusy(true);
    try {
      await saveCompositionTemplate({ type, name: `${channelName} ${noun}`, payload, channelId, setAsDefault: true });
      cache?.cacheChannel(user, channelId, payload);
      toast.success(`Saved as ${channelName}'s ${noun}`);
      refreshTemplates();
    } catch (err) {
      cache?.cacheChannel(user, channelId, payload);
      toast.error(err.response?.data?.error || `Saved on this device only — could not sync ${channelName}`);
    } finally {
      setBusy(false);
    }
  };

  const saveDefault = async () => {
    const payload = buildPayload();
    setBusy(true);
    try {
      await saveCompositionTemplate({ type, name: `Account default ${noun}`, payload, setAsDefault: true });
      cache?.cacheAccount(user, payload);
      toast.success(`Account default ${noun} saved`);
      refreshTemplates();
    } catch (err) {
      cache?.cacheAccount(user, payload);
      toast.error(err.response?.data?.error || 'Saved on this device only — could not sync to your account');
    } finally {
      setBusy(false);
    }
  };

  const loadDefault = async () => {
    setBusy(true);
    const parsed = await loadSavedDesign({ type, user, parse: parsePayload });
    setBusy(false);
    if (parsed) { onApply(parsed); toast.success(`Account default ${noun} loaded`); }
    else toast.info(`No account default ${noun} saved yet`);
  };

  const saveNamed = async () => {
    const n = name.trim();
    if (!n) { toast.error('Give the template a name'); return; }
    try {
      const res = await saveCompositionTemplate({ type, name: n, payload: buildPayload(), templateId: activeId || undefined });
      setActiveId(res?.template_id || activeId);
      toast.success(activeId ? 'Template updated' : 'Template saved');
      refreshTemplates();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save template');
    }
  };

  const loadNamed = (tpl) => {
    const parsed = parsePayload(tpl.payload);
    if (!parsed) { toast.error('That template is empty'); return; }
    onApply(parsed);
    setActiveId(tpl._id);
    setName(tpl.name || '');
    setOpen(false);
    toast.success(`Loaded "${tpl.name || 'Untitled'}"`);
  };

  const removeNamed = async (tpl) => {
    try {
      await deleteCompositionTemplate(tpl._id);
      setTemplates((rows) => rows.filter((t) => t._id !== tpl._id));
      if (activeId === tpl._id) setActiveId(null);
      toast.success('Template deleted');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not delete template');
    }
  };

  const channelLabel = (tpl) => {
    if (!tpl.channel_id) return tpl.is_default ? 'account default' : 'template';
    const ch = channels.find((c) => c._id === tpl.channel_id);
    return ch ? `${ch.channelName}` : 'channel';
  };

  return (
    <div className="st-tplbar">
      <div className="st-tplbar__channel">
        <select
          className="st-select st-select--sm"
          value={channelId}
          onChange={(e) => setChannelId(e.target.value)}
          aria-label="Channel"
          title="Each channel can have its own saved design"
        >
          <option value="">Channel…</option>
          {channels.map((c) => <option key={c._id} value={c._id}>{c.channelName}</option>)}
        </select>
        <button type="button" className="st-btn st-btn--sm" onClick={loadChannel} disabled={busy || !channelId} title="Load this channel's design">
          <FaFolderOpen aria-hidden="true" /><span>Load</span>
        </button>
        <button type="button" className="st-btn st-btn--sm st-btn--primary" onClick={saveChannel} disabled={busy || !channelId} title="Save as this channel's design">
          <FaSave aria-hidden="true" /><span>Save to channel</span>
        </button>
      </div>

      <div className="st-tplbar__more" ref={popRef}>
        <button type="button" className="st-btn st-btn--sm" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          <FaStar aria-hidden="true" /><span>Templates</span>
          {templates.length > 0 && <span className="st-badge">{templates.length}</span>}
        </button>
        {open && (
          <div className="st-pop" role="dialog" aria-label={`${noun} templates`}>
            <div className="st-pop__row">
              <button type="button" className="st-btn st-btn--sm" onClick={loadDefault} disabled={busy}>Load account default</button>
              <button type="button" className="st-btn st-btn--sm st-btn--accent" onClick={saveDefault} disabled={busy}>Save as account default</button>
            </div>
            <div className="st-pop__row">
              <input
                className="st-input"
                placeholder="Template name…"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveNamed(); }}
              />
              <button type="button" className="st-btn st-btn--sm st-btn--primary" onClick={saveNamed}>{activeId ? 'Update' : 'Save'}</button>
            </div>
            <div className="st-pop__list">
              {loadingTemplates && <p className="st-muted">Loading…</p>}
              {!loadingTemplates && templates.length === 0 && <p className="st-muted">No saved {noun}s yet.</p>}
              {templates.map((tpl) => (
                <div key={tpl._id} className={`st-tpl ${activeId === tpl._id ? 'is-active' : ''}`}>
                  <button type="button" className="st-tpl__load" onClick={() => loadNamed(tpl)}>
                    <strong>{tpl.name || 'Untitled'}</strong>
                    <span>{channelLabel(tpl)}{tpl.is_default && tpl.channel_id ? ' · default' : ''}</span>
                  </button>
                  <button type="button" className="st-icon-btn is-danger" onClick={() => removeNamed(tpl)} aria-label={`Delete ${tpl.name}`}>
                    <FaTrash aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
